import { CheckCircleIcon, ChevronRightIcon, FileTextIcon, PlayIcon, TimerIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { twMerge } from 'tailwind-merge';
import modalStore from '../../../stores/modalStore';
import type { ChapterStore } from '../provider/ChapterProvider';
import type { LessonStore } from '../provider/LessonProvider';
import _ from 'lodash';

/**
 * 초를 "X분 Y초" 형식으로 변환
 */
const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    if (mins === 0) {
        return `${secs}초`;
    }
    if (secs === 0) {
        return `${mins}분`;
    }
    return `${mins}분 ${secs}초`;
};

/**
 * 챕터 완료 모달 컨텐츠
 * - 성취감 제공: 완료 메시지, 정량적 정보
 * - 복습 기회: 챕터 영상 다시보기
 * - 진행률 시각화: 프로그레스 바
 * - 다음 학습 유도: 다음 챕터 미리보기
 */
const ChapterCompleteModalContent = observer(
    ({
        modalId,
        lessonStore,
        chapterStore,
    }: {
        modalId: string;
        // 모달이라서 상위 Provider 사용 불가능하여 직접 전달
        lessonStore: LessonStore;
        chapterStore: ChapterStore;
    }) => {
        const chapter = chapterStore.chapter;

        // 다음 챕터 정보 (currentChapterNum은 1-based이므로 그대로 다음 인덱스)
        const nextChapterIndex = lessonStore.currentChapterNum;
        const nextChapter = lessonStore.chapterList[nextChapterIndex];

        // 진행률 계산
        const completedCount = lessonStore.currentChapterNum;
        const totalCount = lessonStore.chapterList.length;
        const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

        if (!chapter) {
            return null;
        }

        return (
            <div className="flex flex-col gap-6">
                {/* 완료 메시지 */}
                <div className="text-center">
                    <div className="text-primary mb-2 flex items-center justify-center gap-2">
                        <CheckCircleIcon className="size-6" />
                        <span className="text-xl font-bold">챕터 {chapter.num} 완료!</span>
                    </div>

                    {/* 챕터 제목 */}
                    <div className="mt-4">
                        <p className="text-base-content text-lg font-medium tracking-tight">
                            {chapter.title}
                        </p>
                        <p className="text-base-content/70 mt-1 text-sm">{chapter.titleKo}</p>
                    </div>
                </div>

                {/* 정량적 정보 */}
                <div className="bg-base-200 rounded-lg p-4">
                    <div className="flex items-center justify-center gap-6">
                        <div className="flex items-center gap-1.5">
                            <FileTextIcon className="text-primary size-4" />
                            <span className="text-base-content text-sm">
                                {chapterStore.sentenceCount}개 문장
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <TimerIcon className="text-primary size-4" />
                            <span className="text-base-content text-sm">
                                약 {formatDuration(chapter.duration ?? 0)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* 챕터 영상 재생 버튼 (자동 재생 모드) */}
                <button
                    className="btn btn-outline btn-block"
                    onClick={() => {
                        // 자동 재생 모드 시작
                        chapterStore.startAutoPlay();

                        // 챕터 시작부터 연속 재생
                        lessonStore.lessonPlayerRef?.current?.seekToAndPlay(chapter.startTime ?? 0);

                        modalStore.close(modalId);
                    }}
                >
                    <PlayIcon className="size-4" />
                    챕터 영상 다시보기
                </button>

                {/* 전체 진행률 */}
                <div>
                    <p className="text-base-content/70 mb-2 text-sm">전체 진행률</p>
                    <div className="bg-base-300 h-2.5 overflow-hidden rounded-full">
                        <div
                            className={twMerge('bg-primary h-full transition-all duration-500')}
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                    <p className="text-base-content/70 mt-1 text-right text-sm tabular-nums">
                        {completedCount}/{totalCount} 챕터
                    </p>
                </div>

                {/* 다음 챕터 정보 및 시작 버튼 */}
                {nextChapter && (
                    <div className="border-base-200 border-t pt-4">
                        <p className="text-base-content/70 mb-2 text-sm">다음 챕터</p>
                        <div className="mb-4">
                            <p className="text-base-content font-medium">
                                챕터 {nextChapter.num}: {nextChapter.title}
                            </p>
                            <p className="text-base-content/70 text-sm">{nextChapter.titleKo}</p>
                        </div>
                        <button
                            className="btn btn-primary btn-block"
                            onClick={() => {
                                lessonStore.nextChapter();
                                modalStore.close(modalId);
                            }}
                        >
                            다음 챕터 시작하기
                            <ChevronRightIcon className="size-4" />
                        </button>
                    </div>
                )}
            </div>
        );
    },
);

export default ChapterCompleteModalContent;
